import { useEffect, useState } from 'react'
import { api } from '../api'
import { navigate } from '../router'
import { formatCents } from '../../shared/money'
import { formatHours } from '../../shared/duration'
import type { Client } from '../../shared/types'
import { pushError } from '../toast'

interface Summary {
  client_id: number
  month_minutes: number
  uninvoiced_cents: number
}

export default function ClientList() {
  const [clients, setClients] = useState<Client[]>([])
  const [summary, setSummary] = useState<Record<number, Summary>>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      try {
        const [cs, sum] = await Promise.all([api.listClients(false), api.summary()])
        setClients(cs)
        const map: Record<number, Summary> = {}
        for (const s of sum) map[s.client_id] = s
        setSummary(map)
      } catch (e) {
        pushError(`Couldn't load clients: ${(e as Error).message}`)
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
      <header className="mb-8 flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Clients</h1>
        <button
          className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700"
          onClick={() => navigate('#/client/new')}
        >
          New client
        </button>
      </header>

      {loading ? (
        <p className="text-slate-400">Loading…</p>
      ) : clients.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-12 text-center text-slate-500">
          <p>No clients yet.</p>
          <button
            className="mt-3 font-medium text-[var(--accent)] underline"
            onClick={() => navigate('#/client/new')}
          >
            Add your first client
          </button>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {clients.map((c) => {
            const s = summary[c.id]
            const accent = c.accent_color ?? '#3b6ea5'
            return (
              <li key={c.id}>
                <button
                  className="group flex w-full items-center gap-4 rounded-lg border border-slate-200 bg-white px-4 py-3 text-left hover:border-slate-300 hover:shadow-sm"
                  onClick={() => navigate(`#/client/${c.id}`)}
                >
                  <span
                    className="h-9 w-1.5 shrink-0 rounded-full"
                    style={{ backgroundColor: accent }}
                    aria-hidden
                  />
                  <span className="flex-1">
                    <span className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">{c.simple_name}</span>
                      {c.archived ? (
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">
                          archived
                        </span>
                      ) : null}
                    </span>
                    <span className="text-xs text-slate-500">{c.legal_name}</span>
                  </span>
                  <span className="tabular text-right text-sm text-slate-600">
                    <span className="block">{formatHours(s?.month_minutes ?? 0)} h this month</span>
                    <span className="block text-slate-500">
                      {formatCents(s?.uninvoiced_cents ?? 0)} uninvoiced
                    </span>
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
